// A bound book's inserts — the extra pieces bindery puts into each copy: a
// folded poster at the back, a reply card after page 12. A finishing touch on
// the BOOK line, not an assembly, so it renders inside the Inserts toggle's
// panel in the configurator rather than in the Assembly control.
//
// Two origins, one picker. The piece is usually another project on THIS order
// — the poster printed as its own item — so the "Which piece?" select lists the
// order's other SAVED projects first and "Something else" last. A project
// added this session has a synthetic id the write boundary would refuse, the
// same reason KitContentsEditor offers only persisted ones. Picking a project
// freezes its display name onto the row, so the row still reads if that
// project is renamed or removed.
//
// The rows are stored as authored (InsertRow), and every write goes out through
// the caller, which pairs them with their derived per-book count via
// withInsertRows — this component never computes that count itself.

import { useEffect, useRef } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  COVER_STOCK_PAPERS,
  INSERT_INKS,
  INSERT_INK_LABELS,
  INSERT_ORIGIN_LABELS,
  INSERT_PLACEMENTS,
  INSERT_PLACEMENT_LABELS,
  PAPER_LABELS,
  TEXT_PAPERS,
  insertsPerBook,
  type InsertInk,
  type InsertPaper,
  type InsertPlacement,
  type InsertRow,
} from 'shared';

import { formatNumber } from '../i18n/formatters.js';
import { parseWholeNumberInput, WHOLE_NUMBER_INPUT_PROPS } from '../lib/wholeNumberInput.js';
import { RowDisclosure, RowRecap, useCollapsedRows } from './CollapsibleRows.js';
import { MoveDownIcon, MoveUpIcon, RemoveIcon } from './ControlIcons.js';
import { FieldReadBack, readBackValue } from './FieldReadBack.js';
import { InkSwatch } from './InkSwatch.js';
import { newKitContentRowId, type KitContentLineOption } from './KitContentsEditor.js';
import { localizeVocabLabels } from './vocabLabels.js';

// The "Which piece?" select's value for a described row. Never a line id: those
// are UUIDs or synthetic local ids, neither of which can collide with this.
const DESCRIBED = '__described';

// The rows as stored, kept as authored while the customer is still typing. Not
// normalizeInsertRows: that drops a described row with no description yet. The
// write boundary normalizes on save.
export function storedInsertRows(raw: unknown): InsertRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (r): r is InsertRow =>
      typeof r === 'object' &&
      r !== null &&
      typeof (r as { row_id?: unknown }).row_id === 'string',
  );
}

// A row that names nothing — a described row with no description. Dropped on
// save, so it is flagged where the customer can still fix it.
function rowUnnamed(row: InsertRow): boolean {
  return row.origin !== 'quote_line' && !row.description?.trim();
}

// What a row IS, read the way its own picker reads it: the live project name
// while the project is still offered, the description frozen at pick time
// otherwise.
function rowName(row: InsertRow, lines: readonly KitContentLineOption[]): string | undefined {
  const live =
    row.origin === 'quote_line' ? lines.find((l) => l.id === row.line_id)?.label : undefined;
  return live ?? (row.description?.trim() || undefined);
}

// One-line read-back of the whole list — "2 — 11 × 17 poster (back), Reply
// card (after p. 12)". The leading number is the per-book insert count, from
// the same shared insertsPerBook the stored count is derived with. Shared by
// the spec rail and any read-back that has a line rather than a panel.
export function insertsSummaryText(
  raw: unknown,
  t: TFunction,
  lines: readonly KitContentLineOption[] = [],
): string | null {
  const rows = storedInsertRows(raw).filter((r) => !rowUnnamed(r));
  if (rows.length === 0) return null;
  const items = rows.map((row) => {
    const name = rowName(row, lines) ?? t('quote:spec.inserts.unnamed');
    const qty = row.quantity_per_book ?? 1;
    const item =
      qty > 1
        ? t('quote:spec.inserts.summary.itemQty', { quantity: formatNumber(qty), name })
        : name;
    if (!row.placement) return item;
    const placement =
      row.placement === 'after_page'
        ? row.placement_page !== undefined
          ? t('quote:spec.inserts.summary.placement.afterPageN', { page: row.placement_page })
          : t('quote:spec.inserts.summary.placement.after_page')
        : t(`quote:spec.inserts.summary.placement.${row.placement}`);
    return t('quote:spec.inserts.summary.itemPlaced', { item, placement });
  });
  return t('quote:spec.inserts.summary.total', {
    count: formatNumber(insertsPerBook(rows)),
    items: items.join(', '),
  });
}

type InsertsEditorProps = {
  rows: readonly InsertRow[];
  onChange: (rows: InsertRow[]) => void;
  // The order's OTHER saved projects — this book is never offered as its own
  // insert. Same list, same labels, as an assembly item's kit rows point at.
  lines: readonly KitContentLineOption[];
  // True when the order carries projects that are not saved yet, so the picker
  // cannot offer them.
  hasUnsavedLines?: boolean;
  // The DOM id SchemaForm gives this field. Every row id hangs off it, so two
  // books on one order never put the same row id in the document twice.
  domId: string;
  // The field's own label, as the list's accessible name: FieldRow prints it as
  // the heading above, and a <div> is not a labelable element.
  label: string;
  describedBy?: string | undefined;
  // A submitted order: every row reads back as its answers, and the add /
  // reorder / remove controls go — an action carries no answer to read back.
  disabled?: boolean;
};

export function InsertsEditor({
  rows,
  onChange,
  lines,
  hasUnsavedLines = false,
  domId,
  label,
  describedBy,
  disabled = false,
}: InsertsEditorProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const originLabels = localizeVocabLabels(
    'spec.inserts.originOptions',
    INSERT_ORIGIN_LABELS,
    t,
  );
  const placementLabels = localizeVocabLabels(
    'spec.inserts.placementOptions',
    INSERT_PLACEMENT_LABELS,
    t,
  );
  const inkLabels = localizeVocabLabels('spec.inserts.inkOptions', INSERT_INK_LABELS, t);
  const paperLabels = localizeVocabLabels('spec.inserts.paperOptions', PAPER_LABELS, t);
  const addRef = useRef<HTMLButtonElement>(null);
  // When Add creates a row, its first control takes focus, so a keyboard or
  // screen-reader user lands on what appeared rather than on the button below it.
  const focusRowId = useRef<string | null>(null);
  useEffect(() => {
    const rowId = focusRowId.current;
    if (!rowId || !rows.some((r) => r.row_id === rowId)) return;
    focusRowId.current = null;
    document.getElementById(`${domId}.${rowId}.piece`)?.focus();
  }, [rows, domId]);
  const collapse = useCollapsedRows(rows.map((r) => r.row_id));

  function patchRow(rowId: string, patch: Partial<InsertRow>): void {
    onChange(rows.map((r) => (r.row_id === rowId ? { ...r, ...patch } : r)));
  }

  // A new insert opens on the first other project, the commonest case by far;
  // with none to point at it opens on "Something else". At the back, because
  // that is where most inserts go.
  function addRow(): void {
    collapse.collapseFinished(rows.filter((r) => !rowUnnamed(r)).map((r) => r.row_id));
    const first = lines[0];
    const row: InsertRow = first
      ? {
          row_id: newKitContentRowId(),
          origin: 'quote_line',
          line_id: first.id,
          description: first.label,
          quantity_per_book: 1,
          placement: 'back',
        }
      : { row_id: newKitContentRowId(), origin: 'described', quantity_per_book: 1, placement: 'back' };
    focusRowId.current = row.row_id;
    onChange([...rows, row]);
  }

  function removeRow(rowId: string): void {
    onChange(rows.filter((r) => r.row_id !== rowId));
    collapse.drop(rowId);
    addRef.current?.focus();
  }

  function moveRow(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    onChange(next);
  }

  // Switching to "Something else" keeps the frozen name as the starting
  // description — it is what the customer last said the piece is. Picking a
  // project clears the row's ink and stock: that project carries its own.
  function pickPiece(row: InsertRow, value: string): void {
    if (value === DESCRIBED) {
      patchRow(row.row_id, { origin: 'described', line_id: undefined });
      return;
    }
    if (value === row.line_id) return;
    const line = lines.find((l) => l.id === value);
    if (!line) return;
    patchRow(row.row_id, {
      origin: 'quote_line',
      line_id: line.id,
      description: line.label,
      ink: undefined,
      paper: undefined,
    });
  }

  // A supplied piece has no stock to choose, so its paper goes with it.
  function pickInk(row: InsertRow, ink: InsertInk): void {
    patchRow(row.row_id, { ink, ...(ink === 'supplied' ? { paper: undefined } : {}) });
  }

  function pickPlacement(row: InsertRow, placement: InsertPlacement): void {
    patchRow(row.row_id, {
      placement,
      ...(placement === 'after_page' ? {} : { placement_page: undefined }),
    });
  }

  return (
    <div
      className="inserts-editor"
      id={domId}
      role="group"
      aria-label={label.trim() ? label : undefined}
      aria-describedby={describedBy}
    >
      {rows.length === 0 &&
        (disabled ? (
          <p className="value--locked">{readBackValue(undefined)}</p>
        ) : (
          <p className="kit-contents__empty">{t('spec.inserts.empty')}</p>
        ))}
      {!disabled && (hasUnsavedLines || lines.length === 0) && (
        <p className="help help--flush">
          {hasUnsavedLines ? t('spec.inserts.unsavedNote') : t('spec.inserts.noOtherItemsNote')}
        </p>
      )}

      {rows.map((row, idx) => {
        const n = idx + 1;
        const rowKey = `${domId}.${row.row_id}`;
        const hintId = `${rowKey}.hint`;
        const bodyId = `${rowKey}.body`;
        const incomplete = !disabled && rowUnnamed(row);
        const folded = !disabled && collapse.isCollapsed(row.row_id);
        const lineId = row.origin === 'quote_line' ? row.line_id : undefined;
        const qty = row.quantity_per_book;
        const placementText = row.placement
          ? row.placement === 'after_page' && row.placement_page !== undefined
            ? t('spec.inserts.afterPageN', { page: row.placement_page })
            : placementLabels[row.placement]
          : undefined;
        return (
          <fieldset
            key={row.row_id}
            className={`kit-row${incomplete ? ' kit-row--incomplete' : ''}${
              folded ? ' kit-row--collapsed' : ''
            }`}
          >
            <legend className="kit-row__legend">
              {!disabled && (
                <RowDisclosure
                  collapsed={folded}
                  bodyId={bodyId}
                  label={
                    folded
                      ? t('spec.inserts.expandAria', { n })
                      : t('spec.inserts.collapseAria', { n })
                  }
                  onToggle={() => collapse.toggle(row.row_id)}
                />
              )}
              {t('spec.inserts.rowLegend', { n })}
            </legend>
            {!disabled && (
              <div className="kit-row__actions">
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('spec.inserts.moveUp', { n })}
                  onClick={() => moveRow(idx, -1)}
                  disabled={idx === 0}
                >
                  <MoveUpIcon />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('spec.inserts.moveDown', { n })}
                  onClick={() => moveRow(idx, 1)}
                  disabled={idx === rows.length - 1}
                >
                  <MoveDownIcon />
                </button>
                <button
                  type="button"
                  className="icon-button icon-button--danger"
                  aria-label={t('spec.inserts.remove', { n })}
                  onClick={() => removeRow(row.row_id)}
                >
                  <RemoveIcon />
                </button>
              </div>
            )}

            {folded && (
              <RowRecap
                text={rowName(row, lines) ?? t('spec.inserts.unnamed')}
                bodyId={bodyId}
                editLabel={t('orderServices.assembly.collapse.edit')}
                onExpand={() => collapse.toggle(row.row_id)}
                meta={[
                  typeof qty === 'number' && qty > 1
                    ? t('orderServices.assembly.collapse.qty', { quantity: formatNumber(qty) })
                    : null,
                  placementText,
                  lineId === undefined && row.ink ? inkLabels[row.ink] : null,
                  lineId === undefined && row.ink !== 'supplied' && row.paper
                    ? paperLabels[row.paper]
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                flag={incomplete ? t('orderServices.assembly.collapse.needsAttention') : undefined}
              />
            )}

            <div id={bodyId} hidden={folded}>
              {disabled ? (
                <FieldReadBack
                  id={`${rowKey}.piece`}
                  label={t('spec.inserts.pieceLabel')}
                  value={lineId !== undefined ? rowName(row, lines) : originLabels.described}
                />
              ) : (
                <div className="form-row">
                  <label htmlFor={`${rowKey}.piece`}>{t('spec.inserts.pieceLabel')}</label>
                  {/* Page order, not alphabetical: a customer picks "Item 2" by
                      where it sits on the page, the same R25 exception the kit
                      rows' project picker takes. The escape hatch trails. */}
                  <select
                    id={`${rowKey}.piece`}
                    value={lineId ?? DESCRIBED}
                    onChange={(e) => pickPiece(row, e.target.value)}
                  >
                    {lines.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.label}
                      </option>
                    ))}
                    {lineId !== undefined && !lines.some((l) => l.id === lineId) && (
                      <option value={lineId}>{row.description ?? lineId}</option>
                    )}
                    <option value={DESCRIBED}>{originLabels.described}</option>
                  </select>
                </div>
              )}

              {lineId === undefined && (
                <div className="form-row">
                  <label htmlFor={`${rowKey}.description`}>
                    {t('spec.inserts.descriptionLabel')}
                    {!disabled && <span className="required-marker"> *</span>}
                  </label>
                  <input
                    id={`${rowKey}.description`}
                    type="text"
                    {...(disabled
                      ? { readOnly: true, 'aria-readonly': true, className: 'input--locked' }
                      : {
                          placeholder: t('spec.inserts.descriptionPlaceholder'),
                          'aria-required': 'true',
                        })}
                    value={disabled ? readBackValue(row.description) : (row.description ?? '')}
                    onChange={(e) => patchRow(row.row_id, { description: e.target.value })}
                    aria-invalid={incomplete ? true : undefined}
                    aria-describedby={incomplete ? hintId : undefined}
                  />
                </div>
              )}

              {lineId === undefined &&
                (disabled ? (
                  <FieldReadBack
                    id={`${rowKey}.ink`}
                    label={t('spec.inserts.inkLabel')}
                    value={row.ink ? inkLabels[row.ink] : undefined}
                  />
                ) : (
                  <div className="form-row">
                    <span className="field-readback__label" id={`${rowKey}.ink.label`}>
                      {t('spec.inserts.inkLabel')}
                    </span>
                    {/* Same cards, same swatch, as the line's own ink pickers. A
                        described insert is one flat sheet, so nothing creases. */}
                    <div
                      id={`${rowKey}.ink`}
                      className="ink-grid"
                      role="radiogroup"
                      aria-labelledby={`${rowKey}.ink.label`}
                    >
                      {INSERT_INKS.map((ink) => {
                        const selected = row.ink === ink;
                        return (
                          <button
                            key={ink}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={`family-card${selected ? ' family-card--selected' : ''}`}
                            onClick={() => pickInk(row, ink)}
                          >
                            <InkSwatch
                              fieldKey="print_ink"
                              value={ink === 'supplied' ? '0_0' : ink}
                              family={undefined}
                            />
                            <span className="family-card__label">{inkLabels[ink]}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

              {lineId === undefined &&
                row.ink !== undefined &&
                row.ink !== 'supplied' &&
                (disabled ? (
                  <FieldReadBack
                    id={`${rowKey}.paper`}
                    label={t('spec.inserts.paperLabel')}
                    value={row.paper ? paperLabels[row.paper] : undefined}
                  />
                ) : (
                  <div className="form-row">
                    <label htmlFor={`${rowKey}.paper`}>{t('spec.inserts.paperLabel')}</label>
                    {/* Weight order inside each group, as the book's own paper
                        pickers read — see TEXT_PAPERS. */}
                    <select
                      id={`${rowKey}.paper`}
                      value={row.paper ?? ''}
                      onChange={(e) =>
                        patchRow(row.row_id, {
                          paper: e.target.value ? (e.target.value as InsertPaper) : undefined,
                        })
                      }
                    >
                      <option value="">{t('spec.inserts.paperUnsure')}</option>
                      <optgroup label={t('spec.inserts.paperGroups.text')}>
                        {TEXT_PAPERS.map((p) => (
                          <option key={p} value={p}>
                            {paperLabels[p]}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label={t('spec.inserts.paperGroups.cover')}>
                        {COVER_STOCK_PAPERS.map((p) => (
                          <option key={p} value={p}>
                            {paperLabels[p]}
                          </option>
                        ))}
                      </optgroup>
                      <option value="other">{paperLabels.other}</option>
                    </select>
                  </div>
                ))}

              <div className="kit-row__grid">
                <div className="form-row">
                  <label htmlFor={`${rowKey}.quantity_per_book`}>
                    {t('spec.inserts.quantityLabel')}
                  </label>
                  <input
                    id={`${rowKey}.quantity_per_book`}
                    {...(disabled
                      ? {
                          type: 'text',
                          readOnly: true,
                          'aria-readonly': true,
                          className: 'input-compact input--locked',
                          value: formatNumber(qty ?? 1),
                        }
                      : {
                          ...WHOLE_NUMBER_INPUT_PROPS,
                          className: 'input-compact',
                          value: typeof qty === 'number' ? qty : '',
                        })}
                    onChange={(e) => {
                      const next = parseWholeNumberInput(e.target.value);
                      if (next === undefined || next === 0) return;
                      patchRow(row.row_id, { quantity_per_book: next ?? undefined });
                    }}
                    onBlur={() => {
                      if (!disabled && typeof qty !== 'number') {
                        patchRow(row.row_id, { quantity_per_book: 1 });
                      }
                    }}
                  />
                </div>

                {disabled ? (
                  <FieldReadBack
                    id={`${rowKey}.placement`}
                    label={t('spec.inserts.placementLabel')}
                    value={row.placement ? placementLabels[row.placement] : undefined}
                  />
                ) : (
                  <div className="form-row">
                    <label htmlFor={`${rowKey}.placement`}>
                      {t('spec.inserts.placementLabel')}
                    </label>
                    {/* The vocabulary's own order — back leads, as the common
                        case — not alphabetical: a positional set, not a catalog. */}
                    <select
                      id={`${rowKey}.placement`}
                      value={row.placement ?? 'back'}
                      onChange={(e) => pickPlacement(row, e.target.value as InsertPlacement)}
                    >
                      {INSERT_PLACEMENTS.map((p) => (
                        <option key={p} value={p}>
                          {placementLabels[p]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {row.placement === 'after_page' && (
                  <div className="form-row">
                    <label htmlFor={`${rowKey}.placement_page`}>
                      {t('spec.inserts.pageLabel')}
                    </label>
                    <input
                      id={`${rowKey}.placement_page`}
                      {...(disabled
                        ? {
                            type: 'text',
                            readOnly: true,
                            'aria-readonly': true,
                            className: 'input-compact input--locked',
                            value: readBackValue(
                              row.placement_page !== undefined
                                ? formatNumber(row.placement_page)
                                : undefined,
                            ),
                          }
                        : {
                            ...WHOLE_NUMBER_INPUT_PROPS,
                            className: 'input-compact',
                            value: row.placement_page ?? '',
                          })}
                      onChange={(e) => {
                        const next = parseWholeNumberInput(e.target.value);
                        if (next === undefined || next === 0) return;
                        patchRow(row.row_id, { placement_page: next ?? undefined });
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <label htmlFor={`${rowKey}.notes`}>
                  {t('spec.inserts.notesLabel')}
                  {!disabled && <span className="help"> {t('shipTo.fields.optionalSuffix')}</span>}
                </label>
                <input
                  id={`${rowKey}.notes`}
                  type="text"
                  {...(disabled
                    ? { readOnly: true, 'aria-readonly': true, className: 'input--locked' }
                    : { placeholder: t('spec.inserts.notesPlaceholder') })}
                  value={disabled ? readBackValue(row.notes) : (row.notes ?? '')}
                  onChange={(e) => patchRow(row.row_id, { notes: e.target.value })}
                />
              </div>

              {incomplete && (
                <p className="kit-row__hint" id={hintId}>
                  {t('spec.inserts.incomplete')}
                </p>
              )}
            </div>
          </fieldset>
        );
      })}

      {!disabled && (
        <button type="button" ref={addRef} onClick={addRow}>
          {rows.length === 0 ? t('spec.inserts.addFirst') : t('spec.inserts.addAnother')}
        </button>
      )}
    </div>
  );
}
